/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { Box, Typography, Grid, Button } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import WarningIcon from '@mui/icons-material/Warning';
import type {
  AssetWeibullMaintenanceStatus,
  WeibullRow,
  WeibullWorkOrders,
} from './types';
import {
  extractWeibullParams,
  generateWeibullData,
  predictMaintenanceNeeds,
  getAssetsTable,
} from './utils';
import type { Asset } from 'types/asset.types';
import SearchableSelector from 'components/SearchableSelector';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const WeibullSurvivabilityCard = ({
  enableDarkTheme,
  makeModel,
  data,
  selectedComponent,
  selectedSubComponent,
  workOrders,
  assets,
  setShowPopup,
}: {
  enableDarkTheme: boolean;
  makeModel: {
    make: string;
    model: string;
  };
  data: WeibullRow[];
  selectedComponent: string;
  selectedSubComponent: string;
  workOrders: WeibullWorkOrders[];
  assets: Asset[];
  setShowPopup: (
    value: { makeModel: string; data: AssetWeibullMaintenanceStatus[] } | null
  ) => void;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const fontColor = palette.textPrimary;

  const { make, model } = makeModel;

  const makeModelAssets = assets
    .filter((item) => item.make === make && item.model === model)
    .map((item) => item.bumperNumber);

  const filteredData = data.filter(
    (item) =>
      item.make === make &&
      item.model === model &&
      item.components_category === selectedComponent &&
      item.sub_component_category === selectedSubComponent
  );

  const filteredWorkOrders = workOrders.filter((item) => {
    const categories = item.sub_component_categories
      ?.replace(/[[\]]/g, '')
      .split(',')
      .map((s) => s.trim());

    const matchesSubCategory = categories.includes(selectedSubComponent);
    const matchesComponent = item.components_category === selectedComponent;
    const matchesAssetId = makeModelAssets.includes(item.asset_id);

    return matchesSubCategory && matchesComponent && matchesAssetId;
  });

  const maintenanceNeeds = predictMaintenanceNeeds(
    filteredWorkOrders,
    filteredData
  );

  const weibullParams = extractWeibullParams(filteredData);

  const {
    weibullKRepair,
    weibullKReplace,
    weibullLambdaRepair,
    weibullLambdaReplace,
  } = weibullParams;

  const chartData = generateWeibullData(
    weibullLambdaRepair,
    weibullKRepair,
    weibullLambdaReplace,
    weibullKReplace
  );

  const assetsTable = useMemo(
    () => getAssetsTable(filteredData, filteredWorkOrders),
    [filteredData, filteredWorkOrders]
  );

  useEffect(() => {
    if (assetsTable.length > 0) {
      if (
        !selectedAsset ||
        !assetsTable.find((a) => a.asset_id === selectedAsset)
      ) {
        setSelectedAsset(assetsTable[0].asset_id);
      }
    } else {
      setSelectedAsset('');
    }
  }, [assetsTable, selectedComponent, selectedSubComponent]);

  const { nextRepair, nextReplace } = useMemo(() => {
    const assetToUse = assetsTable.find(
      (item) => item.asset_id === selectedAsset
    );
    if (assetToUse) {
      return {
        nextRepair: assetToUse.daysUntilNextRepair,
        nextReplace: assetToUse.daysUntilNextReplace,
      };
    }
    return {
      nextRepair: null,
      nextReplace: null,
    };
  }, [selectedAsset]);

  const handleAssetSelection = (value: string) => {
    setSelectedAsset(value);
  };

  const repairLineValue = nextRepair
    ? nextRepair < 0
      ? 365
      : nextRepair
    : null;
  const replaceLineValue = nextReplace
    ? nextReplace < 0
      ? 365
      : nextReplace
    : null;

  return (
    <Box
      sx={{
        borderRadius: '4px',
        height: '330px',
        border: '1px solid #2F445E',
        padding: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: '600',
              color: fontColor,
            }}
          >
            {`${make}-${model}`.toUpperCase()}
          </Typography>
          <Typography
            sx={{
              color: fontColor,
            }}
          >
            {t('weibull_survivability_curve')}
          </Typography>
        </Box>
        {assetsTable.length > 0 && (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                color: fontColor,
                marginLeft: '10px',
                marginRight: '5px',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {t('assets_2')}
            </Typography>
            <SearchableSelector
              options={assetsTable.map((item) => item.asset_id)}
              selectedValues={[selectedAsset]}
              onSelectionChange={handleAssetSelection}
              placeholder={t('search_asset_2')}
              label={t('asset')}
              multiple={false}
              showSelectAll={false}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          marginTop: '20px',
        }}
      >
        <Grid container spacing={1}>
          <Grid item lg={9} md={9}>
            <div style={{ height: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 25, right: 10, left: -30, bottom: 10 }}
                >
                  <CartesianGrid
                    strokeDasharray="2 2"
                    stroke={palette.cartesianGrid}
                  />
                  <XAxis
                    dataKey="time"
                    type="number"
                    domain={[0, 365]}
                    tickCount={15}
                    tick={{ fontSize: 12 }}
                    label={{
                      value: 'Age in Days',
                      position: 'insideBottom',
                      offset: -5,
                    }}
                    tickFormatter={(v) => `${Math.round(v)}`}
                  />
                  <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />

                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'reliabilityRepair') {
                        return [
                          `${(value * 100).toFixed(2)}%`,
                          'Repair Reliability',
                        ];
                      }
                      if (name === 'reliabilityReplace') {
                        return [
                          `${(value * 100).toFixed(2)}%`,
                          'Replace Reliability',
                        ];
                      }

                      return [value.toFixed(2), name];
                    }}
                    labelFormatter={(label: number) =>
                      `Time: ${Math.round(label)} days`
                    }
                  />

                  {weibullKRepair !== 0 && weibullLambdaRepair !== 0 && (
                    <Line
                      type="monotone"
                      dataKey="reliabilityRepair"
                      name="REPAIR Reliability Curve"
                      stroke="#06b6d4"
                      strokeWidth={1}
                      dot={false}
                    />
                  )}
                  {weibullKReplace !== 0 && weibullLambdaReplace !== 0 && (
                    <Line
                      type="monotone"
                      dataKey="reliabilityReplace"
                      name="REPLACE Reliability Curve"
                      stroke="#ff9800"
                      strokeWidth={2}
                      dot={false}
                    />
                  )}
                  {repairLineValue && (
                    <ReferenceLine
                      x={repairLineValue}
                      stroke="#ef4444"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      label={({ viewBox }) => {
                        const x = viewBox?.x ?? 0;
                        const showOnRight =
                          repairLineValue < 90 && repairLineValue > 0;

                        return (
                          <g
                            transform={`translate(${
                              showOnRight ? x + 2 : x - 2
                            }, 12)`}
                          >
                            <text
                              fill="#ef4444"
                              fontSize={10}
                              textAnchor={showOnRight ? 'start' : 'end'}
                            >
                              <tspan x="0" dy="0">
                                {t('next_repair')}
                              </tspan>
                              <tspan x="0" dy="14">
                                {nextRepair} days
                              </tspan>
                            </text>
                          </g>
                        );
                      }}
                    />
                  )}

                  {replaceLineValue && (
                    <ReferenceLine
                      x={replaceLineValue}
                      stroke="#8b5cf6"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      label={({ viewBox }) => {
                        const x = viewBox?.x ?? 0;
                        const showOnRight =
                          replaceLineValue < 90 && replaceLineValue > 0;

                        return (
                          <g
                            transform={`translate(${
                              showOnRight ? x + 2 : x - 2
                            }, 12)`}
                          >
                            <text
                              fill="#8b5cf6"
                              fontSize={10}
                              textAnchor={showOnRight ? 'start' : 'end'}
                            >
                              <tspan
                                x="0"
                                dy={
                                  replaceLineValue === repairLineValue ? 28 : 0
                                }
                              >
                                {t('next_replace')}
                              </tspan>
                              <tspan x="0" dy="14">
                                {nextReplace} days
                              </tspan>
                            </text>
                          </g>
                        );
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Grid>
          <Grid item lg={3} md={3}>
            <Box
              sx={{
                marginLeft: '20px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#2B1F1F',
                  borderRadius: '20px',
                  border: '1px solid #ff9800',
                  padding: '6px 12px',
                  width: 'fit-content',
                }}
              >
                <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />

                <Typography
                  sx={{
                    color: '#ff9800',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {t('eol_risk')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '12px',
                  marginBottom: '10px',
                  marginTop: '5px',
                }}
              >
                {t('next_30_days')}
              </Typography>
              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  '&::before': {
                    content: '""',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#4a7bff',
                    display: 'inline-block',
                  },
                }}
              >
                {t('repair')}
              </Typography>

              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '14px',
                  marginBottom: '20px',
                }}
              >
                {maintenanceNeeds.repair} assets
              </Typography>
              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  '&::before': {
                    content: '""',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#ff9800',
                    display: 'inline-block',
                  },
                }}
              >
                {t('replace')}
              </Typography>

              <Typography
                sx={{
                  color: fontColor,
                  fontSize: '14px',
                  marginBottom: '10px',
                }}
              >
                {maintenanceNeeds.replace} asset
              </Typography>

              <Button
                sx={{
                  marginTop: '5px',
                  textTransform: 'none',
                  color: fontColor,
                  border: '1px solid #2F445E',
                  padding: '5px 20px 5px 20px',
                }}
                onClick={() => {
                  setShowPopup({
                    makeModel: `${make}-${model}`.toUpperCase(),
                    data: assetsTable,
                  });
                }}
              >
                {t('view_details')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default WeibullSurvivabilityCard;
